/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    label_ids,
    label_match,
    groupBy,
    businessHours,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        label_ids,
        label_match,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getSummary(
    since,
    until,
    // eslint-disable-next-line default-param-last
    type = 'account',
    id,
    groupBy,
    businessHours,
    label_ids,
    label_match
  ) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        label_ids,
        label_match,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getCustomers({
    from,
    to,
    type = 'account',
    id,
    label_ids,
    label_match,
  } = {}) {
    return axios.get(`${this.url}/customers`, {
      params: {
        since: from,
        until: to,
        type,
        id,
        label_ids,
        label_match,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV() {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset() },
    });
  }

  getLabelReports({
    from: since,
    to: until,
    businessHours,
    exportFormat,
    id,
    label_ids,
    label_match,
    include_contact_custom_attributes,
  }) {
    const isExcel = exportFormat === 'xlsx' || exportFormat === 'excel';
    const path = isExcel ? `${this.url}/labels.xlsx` : `${this.url}/labels`;
    return axios.get(path, {
      params: {
        since,
        until,
        business_hours: businessHours,
        export_format: exportFormat,
        id,
        label_ids,
        label_match,
        type: 'label',
        include_contact_custom_attributes,
      },
      responseType: isExcel ? 'arraybuffer' : undefined,
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }

  getTemplateReports({
    from,
    to,
    status,
    templateName,
    inboxId,
    search,
    page = 1,
    perPage = 25,
  } = {}) {
    return axios.get(`${this.url}/templates`, {
      params: {
        since: from,
        until: to,
        status,
        template_name: templateName,
        inbox_id: inboxId,
        search,
        page,
        per_page: perPage,
      },
    });
  }

  getWhatsappCostReports({ from, to, inboxId } = {}) {
    return axios.get(`${this.url}/whatsapp_costs`, {
      params: {
        since: from,
        until: to,
        inbox_id: inboxId,
      },
    });
  }

  getCtwaReports({
    from,
    to,
    inboxId,
    sourceId,
    labelIds,
    labelMatch,
    search,
    page = 1,
    perPage = 25,
    exportFormat,
  } = {}) {
    const isExcel = exportFormat === 'xlsx' || exportFormat === 'excel';
    const path = isExcel ? `${this.url}/ctwa.xlsx` : `${this.url}/ctwa`;
    return axios.get(path, {
      params: {
        since: from,
        until: to,
        inbox_id: inboxId,
        source_id: sourceId,
        label_ids: labelIds,
        label_match: labelMatch,
        search,
        page,
        per_page: perPage,
        export_format: exportFormat,
      },
      responseType: isExcel ? 'arraybuffer' : undefined,
    });
  }
}

export default new ReportsAPI();
